import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { LuEye, LuMail } from "react-icons/lu";

import Button from "../../../components/button/Button";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { WarningExclaimSvg } from "../../../components/svg/WarningExclaimSvg";
import { Attachment } from "../../../factories/emails/models/send-email.model";
import { useAppSelector } from "../../../redux/redux";
import {
  BillingLogo,
  IInvoiceView,
  useGetBillingContentQuery,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetEmailFoldersQuery } from "../../../slices/EmailsSlice";
import { IFirm } from "../../../types/types";
import { buildInvoicePdf, invoiceFileName } from "./invoicePdf";
import { SendInvoiceModal } from "./send/SendInvoiceModal";

const PDFPreview: FC<Props> = ({
  invoice,
  firm,
  paymentsRecieved,
  taxName,
  taxRates,
  taxRows,
  taxBase,
  logo,
  setInvoiceFile,
  setIsOpenCompose,
  variant = "default",
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isEmailIntegrationModalOpen, setIsEmailIntegrationModalOpen] =
    useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const { error: emailFoldersError, isLoading: isLoadingEmailFolders } =
    useGetEmailFoldersQuery(
      {
        userId: user?.id || "",
      },
      {
        skip: !user?.id,
      },
    );

  const emailFoldersErrorMessage = emailFoldersError
    ? String(emailFoldersError)
    : "";

  const isEmailIntegrated =
    Boolean(user?.nylasToken?.email) &&
    emailFoldersErrorMessage !==
      "Grant for this User doesn't exists. Try authorizing again.";

  const { data: content } = useGetBillingContentQuery(
    getLinkedFirm()?.orgId || "",
  );

  const getInvoiceFileName = () => invoiceFileName(invoice?.customer?.name);

  const buildPdfDocument = () =>
    buildInvoicePdf({
      invoice,
      firm,
      paymentsRecieved,
      taxName,
      taxRates,
      taxRows,
      taxBase,
      logoUrl: logo?.imageURI,
      notes: content?.notes,
      footer: content?.footer,
      fileTitle: getInvoiceFileName().replace(".pdf", ""),
    });

  const getPdfBlob = async () => {
    const doc = await buildPdfDocument();
    return doc.output("blob");
  };

  const getPdfBase64 = async () => {
    const doc = await buildPdfDocument();
    const pdfData = doc.output("datauristring");
    const base64PdfData = pdfData.split(",")[1];

    if (!base64PdfData) {
      throw new Error("Error: wrong format PDF");
    }

    return base64PdfData;
  };

  const handleOpenPDF = async () => {
    const blob = await getPdfBlob();
    const nextBlobUrl = URL.createObjectURL(blob);

    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
    }

    setPdfBlob(blob);
    setPdfBlobUrl(nextBlobUrl);
    setShowPopup(true);
  };

  const handleDownloadPDF = async () => {
    const blob = pdfBlob ?? (await getPdfBlob());
    const downloadUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = getInvoiceFileName();

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(downloadUrl);
  };

  const sendInvoice = async () => {
    try {
      const base64Pdf = await getPdfBase64();

      setInvoiceFile({
        content: base64Pdf,
        content_type: "base64",
        filename: getInvoiceFileName(),
      });
    } catch (error: any) {
      console.error("Error with sending invoice:", error.message);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  // Roadmap 1789: Send Invoice first asks how to send it (no-reply by default).
  const handleSendInvoice = () => setIsSendModalOpen(true);

  const sendFromMailbox = async () => {
    if (!isEmailIntegrated) {
      setIsSendModalOpen(false);
      setIsEmailIntegrationModalOpen(true);
      return;
    }

    await sendInvoice();
    setIsSendModalOpen(false);
    setIsOpenCompose(true);
  };

  return (
    <div className={variant === "billing" ? "w-full" : undefined}>
      {variant === "billing" ? (
        <div className={"flex gap-2.5"}>
          <button
            type={"button"}
            disabled={isLoadingEmailFolders}
            onClick={handleSendInvoice}
            className={
              "inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-[9px] border border-[#DFE2E8] bg-white text-[13px] font-semibold text-[#3A4256] hover:bg-[#F7F8FA] disabled:opacity-60"
            }>
            <LuMail size={15} />
            Send Invoice
          </button>
          <button
            type={"button"}
            onClick={handleOpenPDF}
            className={
              "inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-[9px] border border-[#DFE2E8] bg-white text-[13px] font-semibold text-[#3A4256] hover:bg-[#F7F8FA]"
            }>
            <LuEye size={15} />
            View Invoice
          </button>
        </div>
      ) : (
        <div className={"flex gap-4"}>
          <Button
            label={"Send invoice"}
            disabled={isLoadingEmailFolders}
            onClick={handleSendInvoice}
          />
          <Button
            label={"View invoice"}
            onClick={handleOpenPDF}
            extraClasses={"normal-case"}
          />
        </div>
      )}
      {showPopup && (
        <div
          className={`modal ${showPopup ? "modal-open" : "modal-close"}`}
          onClick={() => setShowPopup(false)}>
          <div
            className={
              "modal-box max-h-[80vw] min-h-fit min-w-[80vw] max-w-[80vw]"
            }
            onClick={(e) => e.stopPropagation()}>
            <div className={"mb-4 flex items-center justify-between gap-4"}>
              <div className={"text-lg font-semibold"}>
                {getInvoiceFileName()}
              </div>

              <div className={"flex gap-3"}>
                <Button
                  label={"Download invoice"}
                  onClick={handleDownloadPDF}
                />
              </div>
            </div>

            <iframe
              title={"Invoice"}
              src={`${pdfBlobUrl}#toolbar=0`}
              className={"max-h-full min-w-full"}
              height={"800px"}
            />
          </div>
        </div>
      )}
      {isSendModalOpen && invoice?.id && (
        <SendInvoiceModal
          invoiceId={invoice.id}
          invoice={invoice}
          firm={firm ?? null}
          fileName={getInvoiceFileName()}
          mailboxEmail={isEmailIntegrated ? user?.nylasToken?.email || "" : ""}
          getPdfBase64={getPdfBase64}
          onUseMailbox={sendFromMailbox}
          onClose={() => setIsSendModalOpen(false)}
        />
      )}
      {isEmailIntegrationModalOpen && (
        <SettingsConfirmModal
          noCancel
          confirmText={"OK"}
          onClose={() => setIsEmailIntegrationModalOpen(false)}
          onConfirm={() => setIsEmailIntegrationModalOpen(false)}
          variant={"primary"}
          iconVariant={"warning"}
          confirmButtonStyles={
            "flex w-[200px] justify-center rounded-lg px-4 py-2 disabled:opacity-75"
          }
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"Email integration needed"}
          description={
            "In order to send invoices, you first need to integrate your email to ProCharted. Go to the Mail page on the left menu, then click &quot;Connect&quot;"
          }
        />
      )}
    </div>
  );
};

interface Props {
  invoice?: IInvoiceView;
  firm?: IFirm | null;
  paymentsRecieved: number;
  taxName: string;
  taxRates: number[];
  /** One Summary line per tax (QA batch 17); without it the old combined line. */
  taxRows?: { label: string; amount: number }[];
  taxBase?: number;
  logo: BillingLogo | undefined;
  setInvoiceFile: Dispatch<SetStateAction<Attachment>>;
  sendInvoice?: boolean;
  setIsOpenCompose: Dispatch<SetStateAction<boolean>>;
  /** "billing": the invoice page's Send / View buttons (V49 design). */
  variant?: "default" | "billing";
}

export default PDFPreview;
